import throttle from 'lodash/throttle';

import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { updateInlineSuggestion } from '../commands/commands';
import { getPluginState as getInlineSuggestionsPluginState } from '../plugin-factory';
import type { InlineSuggestion } from '../states';
import { dateSuggestionHandler } from './date/date';
import type { SuggestionHandler, SuggestionMarker } from './types';
import { TextPhraseExtractor } from './utils';
import type { TextPhrase } from './utils';

export class SuggestionManager {
	private THROTTLE_MS: number = 100;

	public static readonly INLINE_SUGGESTION_DECORATION_CLASSES = {
		underline: 'proactive-ai-inline-suggestion-underline',
		highlight: 'proactive-ai-inline-suggestion-highlight',
		toolbarAnchor: 'proactive-ai-inline-suggestion-floating-toolbar-anchor',
		completionHint: 'proactive-ai-inline-suggestion-completion-hint',
	};

	public static readonly DECORATION_TYPES = {
		completionHint: 'completionHint',
	};

	private readonly handlers: SuggestionHandler[] = [dateSuggestionHandler];

	private detectFirstSuggestion(
		state: EditorState,
		cursorPhrases: TextPhrase[],
	): InlineSuggestion | null {
		for (const handler of this.handlers) {
			const match = handler.detect(state, cursorPhrases);
			if (match) {
				const node = handler.createReplacement(match, state.schema);
				return { ...match, suggestedNode: node };
			}
		}
		return null;
	}

	private throttledDetection = throttle(
		(view: EditorView) => {
			try {
				const { state, dispatch } = view;
				const pluginState = getInlineSuggestionsPluginState(state);
				const prevSug = pluginState.inlineSuggestion;
				const prevDecos = pluginState.decorationSet;

				const cursorPhrases = TextPhraseExtractor.getTextPhrasesAroundCursor(state);
				const currSug = this.detectFirstSuggestion(state, cursorPhrases);

				const isNoSuggestionChange = prevSug === null && currSug === null;
				if (isNoSuggestionChange) {
					return;
				}

				const updatedDecos = this.rebuildSuggestionDecorations(currSug, state, prevDecos);
				updateInlineSuggestion(currSug, updatedDecos)(state, dispatch);
			} catch (err) {
				// HOT-118204: swallow uncaught errors for now to prevent collab catchup failures
			}
		},
		this.THROTTLE_MS,
		{ leading: false, trailing: true },
	);

	public scheduleScanForSuggestions(view: EditorView): void {
		this.throttledDetection(view);
	}

	public cancelSuggestionScans(): void {
		this.throttledDetection.cancel();
	}

	private createMarkerDecoration(marker: SuggestionMarker): Decoration {
		const addClass = SuggestionManager.INLINE_SUGGESTION_DECORATION_CLASSES[marker.style];
		const decoClass = [marker.cssClass, addClass].filter(Boolean).join(' ');
		return Decoration.inline(marker.from, marker.to, { class: decoClass.trim() });
	}

	/**
	 * Creates an inline decoration whose DOM element serves as a positioning
	 * anchor for the Suggestion popup (i.e. the floating toolbar).
	 */
	private createToolbarDecoration(from: number, to: number): Decoration {
		return Decoration.inline(from, to, {
			class: SuggestionManager.INLINE_SUGGESTION_DECORATION_CLASSES.toolbarAnchor,
		});
	}

	private createCompletionHintDecorations(
		suggestion: InlineSuggestion,
		state: EditorState,
	): Decoration[] {
		const handler = this.handlers.find((handler) => handler.type === suggestion.type);
		if (handler && handler.createCompletionHint) {
			const hint = handler.createCompletionHint(suggestion, state);
			if (hint) {
				return [
					Decoration.widget(
						suggestion.to,
						() => {
							const container = document.createElement('span');
							container.className = [
								SuggestionManager.INLINE_SUGGESTION_DECORATION_CLASSES.completionHint,
								SuggestionManager.INLINE_SUGGESTION_DECORATION_CLASSES.underline,
								SuggestionManager.INLINE_SUGGESTION_DECORATION_CLASSES.highlight,
							].join(' ');
							const textSpan = document.createElement('span');
							textSpan.textContent = hint.hintText;
							container.appendChild(textSpan);
							return container;
						},
						{ side: 1, type: SuggestionManager.DECORATION_TYPES.completionHint },
					),
				];
			}
		}
		return [];
	}

	private rebuildSuggestionDecorations(
		suggestion: InlineSuggestion | null,
		state: EditorState,
		prevDecoSet: DecorationSet,
	): DecorationSet {
		if (!suggestion) {
			return DecorationSet.empty;
		}

		const regionDecos = prevDecoSet.find(suggestion.from, suggestion.to);
		const cleanSet = prevDecoSet.remove(regionDecos);

		const handler = this.handlers.find((handler) => handler.type === suggestion.type);
		if (!handler) {
			return cleanSet;
		}

		const markers = handler.createMarkers(suggestion, state);
		const markerDecos = markers.map((marker) => this.createMarkerDecoration(marker));
		const toolbarDeco = this.createToolbarDecoration(suggestion.from, suggestion.to);

		const newDecos = [...markerDecos, toolbarDeco];
		const hintDecos = this.createCompletionHintDecorations(suggestion, state);
		newDecos.push(...hintDecos);

		return cleanSet.add(state.doc, newDecos);
	}
}

export const suggestionManager: SuggestionManager = new SuggestionManager();
